import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Megaphone, Star, Tag, ExternalLink, UserPlus, LogIn } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Vista pública del Marketplace SalesPRO.
 * Muestra las promociones aprobadas de todas las empresas afiliadas.
 */
export default function Marketplace() {
  const [items, setItems] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [category, setCategory] = useState('');
  const [features, setFeatures] = useState({ marketplace_enabled: true, prospect_signups_enabled: false, affiliation_requests_enabled: false });
  const [signupOpen, setSignupOpen] = useState(false);
  const [sForm, setSForm] = useState({ email: '', full_name: '', password: '', phone: '' });
  const nav = useNavigate();

  const load = () => api.getPublicMarketplace(category ? { category } : {}).then((r) => { setItems(r.data.items || []); setDisabled(!!r.data.disabled); }).catch(() => setItems([]));
  useEffect(() => { load(); }, [category]);
  useEffect(() => { api.getPublicSaasFeatures().then((r) => setFeatures(r.data)).catch(() => {}); }, []);

  const signup = async () => {
    if (!sForm.email || !sForm.full_name || !sForm.password) { toast.error('Completa correo, nombre y contraseña'); return; }
    try { await api.prospectSignup(sForm); toast.success('¡Registro exitoso! Ya puedes iniciar sesión'); setSignupOpen(false); nav('/login'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Categorías únicas presentes
  const cats = [...new Set(items.map((i) => i.category).filter(Boolean))];

  if (disabled) {
    return <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]"><div className="text-center max-w-md">
      <Megaphone size={48} className="mx-auto text-[#94A3B8] mb-3" />
      <h1 className="font-heading text-2xl font-bold text-[#022C22]">Marketplace no disponible</h1>
      <p className="text-sm text-[#64748B] mt-2">El Super Master ha desactivado temporalmente el marketplace. Vuelve pronto.</p>
      <Link to="/login" className="inline-block mt-4 text-primary hover:underline text-sm">Ir al inicio de sesión</Link>
    </div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FBF7] via-white to-[#F0FBF7]" data-testid="marketplace-public">
      {/* Hero */}
      <header className="bg-[#022C22] text-white">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl font-black flex items-center gap-2"><Megaphone size={32} /> SalesPRO · Marketplace</h1>
            <p className="text-sm text-white/80 mt-2 max-w-xl">Las mejores ofertas de todas las empresas afiliadas al ecosistema SalesPRO. Marcas de confianza, precios únicos.</p>
          </div>
          <div className="flex gap-2">
            {features.prospect_signups_enabled && (
              <Button onClick={() => setSignupOpen(true)} className="bg-primary hover:bg-primary/90" data-testid="marketplace-signup-btn"><UserPlus size={14} className="mr-1" /> Crear cuenta gratis</Button>
            )}
            <Link to="/login"><Button variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white/10"><LogIn size={14} className="mr-1" /> Iniciar sesión</Button></Link>
          </div>
        </div>
      </header>

      {/* Filtros */}
      <div className="max-w-6xl mx-auto px-5 py-4 flex flex-wrap items-center gap-2">
        <Tag size={14} className="text-[#64748B]" />
        <button onClick={() => setCategory('')} className={`text-xs px-3 py-1 rounded-full border ${!category ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`}>Todas</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`text-xs px-3 py-1 rounded-full border capitalize ${category === c ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`}>{c}</button>
        ))}
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-5 pb-20">
        {items.length === 0 ? (
          <div className="text-center py-20 text-[#94A3B8]">
            <Megaphone size={64} className="mx-auto opacity-30 mb-3" />
            <p className="text-sm">Aún no hay promociones publicadas en el marketplace.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => (
              <Card key={p.promotion_id} className="overflow-hidden hover:-translate-y-1 transition-all duration-200 shadow-sm hover:shadow-lg" data-testid={`promo-card-${p.promotion_id}`}>
                {p.image_url ? (
                  <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${p.image_url})` }} />
                ) : (
                  <div className="h-40 bg-gradient-to-br from-primary/20 to-[#022C22]/10 flex items-center justify-center">
                    <Megaphone size={56} className="text-primary/40" />
                  </div>
                )}
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-1">
                    <h3 className="font-bold text-[#022C22] text-sm flex items-center gap-1">{p.featured && <Star size={12} className="text-amber-500 fill-amber-500" />}{p.title}</h3>
                    {p.category && <span className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary rounded-full capitalize whitespace-nowrap">{p.category}</span>}
                  </div>
                  {p.description && <p className="text-xs text-[#64748B] line-clamp-3">{p.description}</p>}
                  <div className="pt-2 border-t flex items-center justify-between text-[11px] text-[#64748B]">
                    <span className="font-semibold text-[#022C22]">{p.tenant_name || 'SalesPRO'}</span>
                    {p.valid_to && <span>hasta {p.valid_to}</span>}
                  </div>
                  {p.cta_url && (
                    <a href={p.cta_url} target="_blank" rel="noopener noreferrer" className="w-full">
                      <Button className="w-full" size="sm" data-testid={`promo-cta-${p.promotion_id}`}><ExternalLink size={12} className="mr-1" /> {p.cta_label || 'Ver oferta'}</Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <footer className="bg-[#022C22] text-white/70 py-6 text-center text-xs">
        <p>© 2026 SalesPRO · Plataforma multi-empresa</p>
      </footer>

      {/* Modal registro prospecto */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-md" data-testid="marketplace-signup-modal">
          <DialogHeader><DialogTitle>Crear cuenta en SalesPRO</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#64748B]">Regístrate gratis para ver las ofertas con descuentos especiales y pedir afiliación a las empresas del marketplace.</p>
            <div><Label className="text-xs">Correo *</Label><Input value={sForm.email} onChange={(e) => setSForm({ ...sForm, email: e.target.value })} /></div>
            <div><Label className="text-xs">Nombre completo *</Label><Input value={sForm.full_name} onChange={(e) => setSForm({ ...sForm, full_name: e.target.value })} /></div>
            <div><Label className="text-xs">Contraseña *</Label><Input type="password" value={sForm.password} onChange={(e) => setSForm({ ...sForm, password: e.target.value })} /></div>
            <div><Label className="text-xs">Teléfono (opcional)</Label><Input value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} placeholder="+58..." /></div>
            <Button onClick={signup} className="w-full" data-testid="signup-submit">Crear mi cuenta</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
